import React, { useMemo, useState } from 'react';
import { useAdminRestaurants } from '../../hooks/useAdminRestaurants.js';
import { useAdminWarmers } from '../../hooks/useAdminWarmers.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const RestaurantsSection = ({ onNotify }) => {
  const {
    items,
    total,
    page,
    perPage,
    loading,
    error,
    search,
    setSearch,
    setPage,
    refresh,
    saveRestaurant,
    deleteRestaurant,
    loadSyncPreview,
    syncSelected,
    syncDivisionAreas
  } = useAdminRestaurants();
  const {
    items: warmers,
    loading: warmersLoading,
    error: warmersError,
    refresh: refreshWarmers
  } = useAdminWarmers({ activeOnly: true });
  const [searchDraft, setSearchDraft] = useState(search);
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });
  const [syncState, setSyncState] = useState({
    open: false,
    loading: false,
    error: null,
    items: [],
    counts: null,
    submitting: false
  });
  const [bohDivisionAreaState, setBohDivisionAreaState] = useState({ loading: false });
  const [exclusionsState, setExclusionsState] = useState({
    open: false,
    loading: false,
    items: [],
    error: null,
    form: { area_id: '', descripcion: '', motivo: '' },
    saving: false,
    editing: null
  });

  useLucideIcons([
    items.length,
    warmers.length,
    editorState.open,
    syncState.open,
    bohDivisionAreaState.loading,
    exclusionsState.open,
    exclusionsState.items.length
  ]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / perPage)), [total, perPage]);

  const handleOpenEditor = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : { nombre: '', id_boh: '', rbi_id: '', fecha_apertura: '', rational: 0, warmer: '', calculo_automatico: false }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveRestaurant(values);
      setEditorState({ open: false, saving: false, record: null });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Restaurante actualizado correctamente.' : 'Restaurante creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el restaurante.' });
    }
  };

  const handleDelete = async (record) => {
    if (!record) {
      return;
    }
    const confirmed = window.confirm(`¿Eliminar el restaurante "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteRestaurant(record.id);
      onNotify?.({ type: 'success', message: 'Restaurante eliminado.' });
      refresh();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el restaurante.' });
    }
  };

  const handleOpenExclusions = () => {
    setExclusionsState((prev) => ({
      ...prev,
      open: true,
      loading: true,
      error: null
    }));
    fetchExclusions();
  };

  const handleOpenSyncModal = async () => {
    setSyncState({
      open: true,
      loading: true,
      error: null,
      items: [],
      counts: null,
      submitting: false
    });
    try {
      const data = await loadSyncPreview();
      const normalized = (data?.sucursales || []).map((item, index) => ({
        key: `${item.id}-${index}`,
        id: item.id,
        nombre: item.nombre || '',
        rbi_id: item.rbi_id || '',
        fecha_apertura: item.fecha_apertura || '',
        rational: 0,
        warmer: '',
        selected: false
      }));
      setSyncState((prev) => ({
        ...prev,
        loading: false,
        items: normalized,
        counts: {
          total: data?.total_count ?? normalized.length,
          new: data?.new_count ?? normalized.length,
          existing: data?.existing_count ?? 0
        }
      }));
    } catch (err) {
      setSyncState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudo cargar la sincronización.'
      }));
    }
  };

  const updateSyncItem = (index, updates) => {
    setSyncState((prev) => ({
      ...prev,
      items: prev.items.map((item, current) => (current === index ? { ...item, ...updates } : item))
    }));
  };

  const toggleSelectAll = (checked) => {
    setSyncState((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({ ...item, selected: checked }))
    }));
  };

  const handleSyncSubmit = async () => {
    const selected = syncState.items.filter((item) => item.selected);
    if (!selected.length) {
      setSyncState((prev) => ({ ...prev, error: 'Selecciona al menos una sucursal para sincronizar.' }));
      return;
    }
    setSyncState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const payload = selected.map((item) => ({
        id_boh: item.id,
        nombre: item.nombre,
        rbi_id: item.rbi_id,
        fecha_apertura: item.fecha_apertura || null,
        rational: Number(item.rational) || 0,
        warmer: Number(item.warmer) || 0
      }));
      const data = await syncSelected(payload);
      const message = [data?.message, (data?.errors || []).join(' ')].filter(Boolean).join(' ');
      onNotify?.({
        type: data?.errors?.length ? 'warning' : 'success',
        message: message || 'Sincronización completada.'
      });
      setSyncState({ open: false, loading: false, error: null, items: [], counts: null, submitting: false });
      refresh();
    } catch (err) {
      setSyncState((prev) => ({ ...prev, submitting: false, error: err?.message || 'Error al sincronizar.' }));
    }
  };

  const handleSyncDivisionAreas = async () => {
    if (bohDivisionAreaState.loading) {
      return;
    }
    const confirmed = window.confirm('¿Sincronizar división y área BoH para los restaurantes existentes?');
    if (!confirmed) {
      return;
    }
    setBohDivisionAreaState({ loading: true });
    try {
      const data = await syncDivisionAreas();
      const message = [data?.message, (data?.errors || []).join(' ')].filter(Boolean).join(' ');
      onNotify?.({
        type: data?.errors?.length ? 'warning' : 'success',
        message: message || 'Sincronización completada.'
      });
      refresh();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo sincronizar áreas y divisiones.' });
    } finally {
      setBohDivisionAreaState({ loading: false });
    }
  };

  const closeSyncModal = () => {
    setSyncState({ open: false, loading: false, error: null, items: [], counts: null, submitting: false });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchDraft(value);
    setSearch(value.trim());
  };

  const handleSearchReset = () => {
    setSearchDraft('');
    setSearch('');
  };

  const fetchExclusions = async () => {
    setExclusionsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch('/admin/api/exclusions');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'No se pudieron cargar las exclusiones.');
      }
      setExclusionsState((prev) => ({
        ...prev,
        loading: false,
        items: data.exclusions || [],
        error: null
      }));
    } catch (err) {
      setExclusionsState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudieron cargar las exclusiones.'
      }));
    }
  };

  const closeExclusionsModal = () => {
    setExclusionsState({
      open: false,
      loading: false,
      items: [],
      error: null,
      form: { area_id: '', descripcion: '', motivo: '' },
      saving: false,
      editing: null
    });
  };

  const handleExclusionFieldChange = (event) => {
    const { name, value } = event.target;
    setExclusionsState((prev) => ({
      ...prev,
      form: { ...prev.form, [name]: value }
    }));
  };

  const resetExclusionForm = () => {
    setExclusionsState((prev) => ({
      ...prev,
      form: { area_id: '', descripcion: '', motivo: '' },
      editing: null
    }));
  };

  const handleSubmitExclusion = async (event) => {
    event.preventDefault();
    if (!exclusionsState.form.area_id) {
      setExclusionsState((prev) => ({ ...prev, error: 'El ID de área es obligatorio.' }));
      return;
    }
    setExclusionsState((prev) => ({ ...prev, saving: true, error: null }));
    const payload = {
      area_id: Number(exclusionsState.form.area_id),
      descripcion: exclusionsState.form.descripcion,
      motivo: exclusionsState.form.motivo
    };
    const editingId = exclusionsState.editing?.id;
    const url = editingId ? `/admin/api/exclusions/${editingId}` : '/admin/api/exclusions';
    const method = editingId ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'No se pudo guardar la exclusión.');
      }
      onNotify?.({
        type: 'success',
        message: editingId ? 'Exclusión actualizada correctamente.' : 'Exclusión creada correctamente.'
      });
      setExclusionsState((prev) => ({
        ...prev,
        saving: false,
        form: { area_id: '', descripcion: '', motivo: '' },
        editing: null
      }));
      fetchExclusions();
    } catch (err) {
      setExclusionsState((prev) => ({
        ...prev,
        saving: false,
        error: err?.message || 'No se pudo guardar la exclusión.'
      }));
    }
  };

  const handleEditExclusion = (exclusion) => {
    setExclusionsState((prev) => ({
      ...prev,
      form: {
        area_id: exclusion.area_id || '',
        descripcion: exclusion.descripcion || '',
        motivo: exclusion.motivo || ''
      },
      editing: exclusion
    }));
  };

  const handleDeleteExclusion = async (exclusion) => {
    if (!exclusion?.id) {
      return;
    }
    const confirmed = window.confirm(`¿Eliminar la exclusión del área ${exclusion.area_id}?`);
    if (!confirmed) {
      return;
    }
    try {
      const response = await fetch(`/admin/api/exclusions/${exclusion.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'No se pudo eliminar la exclusión.');
      }
      onNotify?.({ type: 'success', message: 'Exclusión eliminada correctamente.' });
      fetchExclusions();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar la exclusión.' });
    }
  };

  const renderRows = () => {
    if (!items.length) {
      return (
        <tr>
          <td colSpan={6} className="empty-state">
            <div className="empty-state-icon"><i data-lucide="inbox" /></div>
            <div className="empty-state-text">No se encontraron restaurantes</div>
            <div className="empty-state-subtext">Crea un restaurante o ajusta tu búsqueda.</div>
          </td>
        </tr>
      );
    }
    return items.map((restaurant) => (
      <tr key={restaurant.id}>
        <td>{restaurant.rbi_id || '-'}</td>
        <td><strong>{restaurant.nombre}</strong></td>
        <td>{restaurant.fecha_apertura || '-'}</td>
        <td>
          {restaurant.calculo_automatico ? (
            <span className="status-badge status-active">Sí</span>
          ) : (
            <span className="status-badge status-inactive">No</span>
          )}
        </td>
        <td>
          {restaurant.warmer ? (
            <span className="status-badge status-neutral" title={restaurant.warmer_nombre || undefined}>
              {restaurant.warmer_nombre || `ID ${restaurant.warmer}`}
            </span>
          ) : (
            <span className="muted-text">Sin warmer</span>
          )}
        </td>
        <td>
          <div className="action-buttons">
            <button className="action-btn edit" type="button" onClick={() => handleOpenEditor(restaurant)}>
              <i data-lucide="edit-2" />
            </button>
            <button className="action-btn delete" type="button" onClick={() => handleDelete(restaurant)}>
              <i data-lucide="trash-2" />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <section id="restaurants-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Gestión de Restaurantes</h2>
        <div className="section-actions">
          <form className="search-container" onSubmit={(event) => event.preventDefault()}>
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar restaurantes..."
              value={searchDraft}
              onChange={handleSearchChange}
            />
            {searchDraft ? (
              <button type="button" className="btn-secondary" onClick={handleSearchReset}>
                Limpiar
              </button>
            ) : null}
          </form>
          <div className="button-group">
            <button className="btn-secondary" type="button" onClick={handleOpenExclusions}>
              <span className="btn-icon"><i data-lucide="x-circle" /></span>
              Exclusiones de Áreas
            </button>
            <button className="btn-primary" type="button" onClick={handleOpenSyncModal}>
              <span className="btn-icon"><i data-lucide="refresh-cw" /></span>
              Sincronizar con BoH
            </button>
            <button
              className="btn-secondary"
              type="button"
              onClick={handleSyncDivisionAreas}
              disabled={bohDivisionAreaState.loading}
            >
              <span className="btn-icon"><i data-lucide="link-2" /></span>
              {bohDivisionAreaState.loading ? 'Sincronizando áreas…' : 'Sincronizar áreas/divisiones'}
            </button>
            <button className="btn-primary" type="button" onClick={() => handleOpenEditor()}>
              <span className="btn-icon"><i data-lucide="plus" /></span>
              Nuevo Restaurante
            </button>
          </div>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando restaurantes...</div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>RBI ID</th>
                <th>Nombre</th>
                <th>Fecha apertura</th>
                <th>Calc. automático</th>
                <th>Warmer</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                    <div className="empty-state-subtext">Intenta recargar o verifica tu conexión.</div>
                  </td>
                </tr>
              ) : renderRows()}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />
      {editorState.open ? (
        <RestaurantEditorModal
          state={editorState}
          warmers={warmers}
          warmersLoading={warmersLoading}
          warmersError={warmersError}
          onReloadWarmers={refreshWarmers}
          onClose={() => setEditorState({ open: false, record: null, saving: false })}
          onSubmit={handleSave}
        />
      ) : null}
      {syncState.open ? (
        <SyncBoHModal
          state={syncState}
          warmers={warmers}
          warmersLoading={warmersLoading}
          warmersError={warmersError}
          onReloadWarmers={refreshWarmers}
          onClose={closeSyncModal}
          onToggleAll={toggleSelectAll}
          onUpdateItem={updateSyncItem}
          onSync={handleSyncSubmit}
        />
      ) : null}
      {exclusionsState.open ? (
        <AreaExclusionsModal
          state={exclusionsState}
          onClose={closeExclusionsModal}
          onFieldChange={handleExclusionFieldChange}
          onSubmit={handleSubmitExclusion}
          onRefresh={fetchExclusions}
          onEdit={handleEditExclusion}
          onDelete={handleDeleteExclusion}
          onResetForm={resetExclusionForm}
        />
      ) : null}
    </section>
  );
};

const Pagination = ({ page, totalPages, total, perPage, onPageChange }) => (
  <div className="pagination-container">
    <div className="pagination-info">
      <span>Página {page} de {totalPages}</span>
      <span className="pagination-total">
        {total.toLocaleString()} registros · {perPage} por página
      </span>
    </div>
    <div className="pagination-actions">
      <button
        type="button"
        className="pagination-btn"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <i data-lucide="chevron-left" />
      </button>
      <button
        type="button"
        className="pagination-btn"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <i data-lucide="chevron-right" />
      </button>
    </div>
  </div>
);

const RestaurantEditorModal = ({ state, warmers, warmersLoading, warmersError, onReloadWarmers, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    warmer: '',
    ...state.record,
    calculo_automatico: Boolean(state?.record?.calculo_automatico)
  }));

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>{formValues?.id ? 'Editar Restaurante' : 'Nuevo Restaurante'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>ID BoH</label>
            <input
              type="number"
              className="form-input"
              name="id_boh"
              value={formValues.id_boh || ''}
              onChange={handleChange}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>RBI ID</label>
            <input
              type="text"
              className="form-input"
              name="rbi_id"
              value={formValues.rbi_id || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Fecha apertura</label>
            <input
              type="date"
              className="form-input"
              name="fecha_apertura"
              value={formValues.fecha_apertura || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Rational</label>
            <input
              type="number"
              className="form-input"
              name="rational"
              value={formValues.rational ?? 0}
              onChange={handleChange}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Cálculo automático</label>
            <select
              className="form-select"
              name="calculo_automatico"
              value={Boolean(formValues.calculo_automatico) ? 'si' : 'no'}
              onChange={(event) => setFormValues((prev) => ({ ...prev, calculo_automatico: event.target.value === 'si' }))}
            >
              <option value="si">Sí (usar semanas por defecto)</option>
              <option value="no">No (permitir elegir semanas)</option>
            </select>
            <p className="helper-text">"Sí" bloquea la selección manual de semanas y usa el rango por defecto.</p>
          </div>
          <div className="form-group">
            <label>Warmer</label>
            <select
              className="form-select"
              name="warmer"
              value={formValues.warmer ?? ''}
              onChange={handleChange}
              disabled={warmersLoading}
            >
              <option value="">Sin warmer</option>
              {warmers.map((warmer) => (
                <option key={warmer.id} value={warmer.id}>
                  {warmer.nombre}
                </option>
              ))}
            </select>
            {warmersLoading ? (
              <div className="empty-state-subtext">Cargando warmers...</div>
            ) : null}
            {warmersError ? (
              <div className="form-error">
                {warmersError}{' '}
                <button className="btn-secondary" type="button" onClick={onReloadWarmers}>Reintentar</button>
              </div>
            ) : null}
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SyncBoHModal = ({
  state,
  warmers,
  warmersLoading,
  warmersError,
  onReloadWarmers,
  onClose,
  onToggleAll,
  onUpdateItem,
  onSync
}) => {
  const selectedCount = state.items.filter((item) => item.selected).length;
  const warmerOptions = warmers || [];

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h3>
            <i data-lucide="refresh-cw" />
            Sincronizar con BoH
          </h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <div className="modal-body">
          {state.loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Buscando nuevas sucursales…</div>
            </div>
          ) : (
            <>
              <SyncSummary counts={state.counts} />
              {state.error ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                  <div className="empty-state-text">{state.error}</div>
                </div>
              ) : (
                <>
                  {warmersError ? (
                    <div className="form-error">
                      {warmersError}{' '}
                      <button className="btn-secondary" type="button" onClick={onReloadWarmers}>Reintentar</button>
                    </div>
                  ) : null}
                  <div className="sync-select-all-container">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={state.items.length > 0 && selectedCount === state.items.length}
                        onChange={(event) => onToggleAll(event.target.checked)}
                      />
                      <span className="checkmark" />
                      Seleccionar todo
                    </label>
                    <span className="selected-count">{selectedCount} seleccionadas</span>
                  </div>
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th />
                          <th>ID BoH</th>
                          <th>Nombre</th>
                          <th>RBI ID</th>
                          <th>Fecha apertura</th>
                          <th>Rational</th>
                          <th>Warmer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.items.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="empty-state">
                              <div className="empty-state-icon"><i data-lucide="check-circle" /></div>
                              <div className="empty-state-text">No hay nuevas sucursales para sincronizar.</div>
                            </td>
                          </tr>
                        ) : (
                          state.items.map((item, index) => (
                            <tr key={item.key}>
                              <td>
                                <label className="checkbox-item">
                                  <input
                                    type="checkbox"
                                    checked={item.selected}
                                    onChange={(event) => onUpdateItem(index, { selected: event.target.checked })}
                                  />
                                  <span className="checkmark" />
                                </label>
                              </td>
                              <td>{item.id}</td>
                              <td><strong>{item.nombre}</strong></td>
                              <td>{item.rbi_id || '-'}</td>
                              <td>{item.fecha_apertura || '-'}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-input"
                                  min="0"
                                  value={item.rational}
                                  onChange={(event) => onUpdateItem(index, { rational: event.target.value })}
                                />
                              </td>
                              <td>
                                <select
                                  className="form-select"
                                  value={item.warmer ?? ''}
                                  onChange={(event) => onUpdateItem(index, { warmer: event.target.value })}
                                  disabled={warmersLoading}
                                >
                                  <option value="">Sin warmer</option>
                                  {warmerOptions.map((warmer) => (
                                    <option key={warmer.id} value={warmer.id}>
                                      {warmer.nombre}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            type="button"
            disabled={state.loading || state.submitting || selectedCount === 0}
            onClick={onSync}
          >
            {state.submitting ? 'Sincronizando…' : `Sincronizar ${selectedCount > 0 ? `${selectedCount} sucursales` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const AreaExclusionsModal = ({
  state,
  onClose,
  onFieldChange,
  onSubmit,
  onRefresh,
  onEdit,
  onDelete,
  onResetForm
}) => (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-content large-modal">
      <div className="modal-header">
        <h3>
          <i data-lucide="shield-x" />
          Gestión de Exclusiones de Áreas
        </h3>
        <button className="modal-close" type="button" onClick={onClose}>
          <i data-lucide="x" />
        </button>
      </div>
      <div className="modal-body">
        <section className="exclusions-form">
          <h4>{state.editing ? `Editar exclusión #${state.editing.id}` : 'Agregar nueva exclusión'}</h4>
          <form className="form-grid" onSubmit={onSubmit}>
            <div className="form-group">
              <label htmlFor="exclusion-area-id">ID de Área *</label>
              <input
                id="exclusion-area-id"
                name="area_id"
                className="form-input"
                placeholder="Ingresa el ID del área a excluir"
                value={state.form.area_id}
                onChange={onFieldChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="exclusion-descripcion">Descripción</label>
              <input
                id="exclusion-descripcion"
                name="descripcion"
                className="form-input"
                placeholder="Descripción opcional"
                value={state.form.descripcion}
                onChange={onFieldChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="exclusion-motivo">Motivo</label>
              <input
                id="exclusion-motivo"
                name="motivo"
                className="form-input"
                placeholder="Motivo de la exclusión"
                value={state.form.motivo}
                onChange={onFieldChange}
              />
            </div>
            <div className="form-group button-group">
              <button className="btn-secondary" type="button" onClick={onResetForm} disabled={state.saving}>
                Limpiar
              </button>
              <button className="btn-primary" type="submit" disabled={state.saving}>
                {state.saving ? 'Guardando…' : state.editing ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </form>
          {state.error ? (
            <div className="form-error" role="alert">
              {state.error}
            </div>
          ) : null}
        </section>
        <section className="exclusions-list section-spacer">
          <div className="section-header">
            <h4>Exclusiones configuradas</h4>
            <button className="btn-secondary" type="button" onClick={onRefresh} disabled={state.loading}>
              {state.loading ? 'Actualizando…' : 'Refrescar'}
            </button>
          </div>
          <div className="data-table-container">
            {state.loading ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <div className="loading-text">Cargando exclusiones...</div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Área</th>
                    <th>Descripción</th>
                    <th>Motivo</th>
                    <th>Creado por</th>
                    <th>Creado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {state.items.length ? (
                    state.items.map((exclusion) => (
                      <tr key={exclusion.id}>
                        <td>{exclusion.area_id}</td>
                        <td>{exclusion.descripcion || 'Sin descripción'}</td>
                        <td>{exclusion.motivo || 'Sin motivo'}</td>
                        <td>{exclusion.created_by || 'Desconocido'}</td>
                        <td>{exclusion.created_at ? new Date(exclusion.created_at).toLocaleString() : '—'}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn edit" type="button" onClick={() => onEdit(exclusion)}>
                              <i data-lucide="edit-2" />
                            </button>
                            <button className="action-btn delete" type="button" onClick={() => onDelete(exclusion)}>
                              <i data-lucide="trash-2" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        <div className="empty-state-icon"><i data-lucide="shield" /></div>
                        <div className="empty-state-text">No hay exclusiones configuradas.</div>
                        <div className="empty-state-subtext">Agrega una exclusión para comenzar.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" type="button" onClick={onClose}>Cerrar</button>
      </div>
    </div>
  </div>
);

const SyncSummary = ({ counts }) => {
  if (!counts) {
    return null;
  }
  return (
    <div className="sync-stats">
      <span className="stat-item">
        <i data-lucide="database" />
        <strong>{counts.total}</strong> totales
      </span>
      <span className="stat-item new">
        <i data-lucide="plus-circle" />
        <strong>{counts.new}</strong> nuevas
      </span>
      <span className="stat-item">
        <i data-lucide="check-circle" />
        <strong>{counts.existing}</strong> sincronizadas
      </span>
    </div>
  );
};

export default RestaurantsSection;
